import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Leaf, Sparkles, Truck, ShieldCheck, ArrowDown } from "lucide-react";

export const PRODUCT_CLEAN = "/product-clean.webp";
export const HERO_SCENE =
  "https://customer-assets-jt897jd0.emergentagent.net/job_d3413f80-a025-4b98-a005-3179aa5bd4e5/artifacts/882c9573d40560fe_Gemini_Generated_Image_swzcv4swzcv4swzc.webp";
export const PROMO_POLAROIDS =
  "https://customer-assets-jt897jd0.emergentagent.net/job_d3413f80-a025-4b98-a005-3179aa5bd4e5/artifacts/581c7d917ed5946e_ChatGPT%20Image%206%20oct.%202026%2C%2022_22_10.png";
export const PROMO_PROGRESSION = "/flyer.webp";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const badges = [
  { icon: Leaf, label: "طبيعي 100%" },
  { icon: Sparkles, label: "بدون عطر" },
  { icon: Truck, label: "توصيل لكل المدن" },
  { icon: ShieldCheck, label: "الدفع عند الاستلام" },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 140, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" className="grain relative overflow-hidden pt-28 pb-16 sm:pt-36 lg:pb-24">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#2E9E6B]/25 bg-[#E8F5EF] px-4 py-1.5 text-xs font-semibold text-[#1F7A52]"
            data-testid="hero-badge"
          >
            <Leaf className="h-3.5 w-3.5" />
            بخاخ مرمّم للأظافر والقدمين · 100 مل
          </motion.div>

          <h1 className="font-display text-4xl font-black leading-[1.15] tracking-tight text-[#0F1E19] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>العناية بأظافرك</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-[#1F7A52]">الصحية تبدأ</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>من اليوم.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-[#566B63] sm:text-lg"
          >
            تركيبة طبيعية متطوّرة تجمع حمض الساليسيليك الفعّال مع خلاصة اللافندر
            والأوكالبتوس وشجرة الشاي والزعتر — تخترق عمق الظفر لتنقّيته من الفطريات
            وتساعده على استعادة مظهره الصحي، بقوام خفيف يجف في ثوانٍ.
            <span className="font-serif-ar mt-2 block text-[#1F7A52]">
              عناية تصنع الفرق ✦
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#order"
              data-testid="hero-order-button"
              className="group rounded-full bg-[#1F7A52] px-8 py-4 text-base font-bold text-[#FBF9F4] transition-all duration-300 hover:-translate-y-1 hover:bg-[#0F1E19] hover:shadow-xl hover:shadow-emerald-900/25"
            >
              اطلب الآن · 129 درهم
            </a>
            <a
              href="#benefits"
              data-testid="hero-discover-button"
              className="rounded-full border border-[#0F1E19]/15 px-8 py-4 text-base font-bold text-[#0F1E19] transition-all duration-300 hover:border-[#1F7A52] hover:text-[#1F7A52]"
            >
              اكتشف التركيبة
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
            data-testid="hero-trust-badges"
          >
            {badges.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-xl border border-[#2E9E6B]/15 bg-white/70 px-3 py-2.5 text-xs font-semibold text-[#0F1E19]"
              >
                <Icon className="h-4 w-4 shrink-0 text-[#2E9E6B]" />
                {label}
              </div>
            ))}
          </motion.div>
        </div>

        <div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={() => {
            mx.set(0);
            my.set(0);
          }}
          className="relative mx-auto w-full max-w-md"
          style={{ perspective: 900 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative overflow-hidden rounded-[2rem] border border-[#2E9E6B]/15 bg-white shadow-2xl shadow-emerald-900/15"
              data-testid="hero-product-image"
            >
              <img
                src={PRODUCT_CLEAN}
                alt="بخاخ يوكيور بروفيشينال المرمّم للأظافر والقدمين مع علبته"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, rotate: -12, scale: 0.8 }}
              animate={{ opacity: 1, rotate: -8, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="absolute -top-5 -right-4 rounded-2xl bg-[#0F1E19] px-5 py-3 text-center shadow-xl"
              style={{ transform: "translateZ(50px)" }}
              data-testid="hero-price-badge"
            >
              <span className="block text-2xl font-black text-[#D4AF37]">129</span>
              <span className="block text-xs font-bold text-[#FBF9F4]">درهم فقط</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.45 }}
              className="absolute -bottom-5 left-6 rounded-2xl border border-[#D4AF37]/40 bg-[#FBF9F4] px-5 py-2.5 text-xs font-bold tracking-[0.25em] text-[#1F7A52] shadow-lg"
              style={{ transform: "translateZ(40px)" }}
              dir="ltr"
            >
              STOP · CURE · CARE
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="mt-16 flex justify-center"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-[#2E9E6B]" />
      </motion.div>
    </section>
  );
}
